import { Button } from '@/components/Button/Button'
import { Input } from '@/components/Input/Input'
import { Loader } from '@/components/Loader/Loader'
import { Popup } from '@/components/Popup/Popup'
import { TextArea } from '@/components/TextArea/TextArea'
import { GeneratorFormContext } from '@/hooks/useGeneratorForm'
import { useProjectsState } from '@/state/projects'
import type { FunctionalComponent } from 'preact'
import { useContext, useState } from 'preact/hooks'
import { useShallow } from 'zustand/shallow'

export const ProjectForm: FunctionalComponent<{
  type: 'create' | 'edit'
  onSuccess: () => void
  onCancel?: () => void
  onDelete?: () => void
}> = props => {
  const form = useContext(GeneratorFormContext)!
  const [loading, setLoading] = useState(false)

  const project = useProjectsState(useShallow(
    s => s.list.find(item => item.id === form.projectId),
  ))

  const [title, setTitle] = useState(
    (props.type === 'edit' ? project?.title : '') ?? '',
  )

  const [description, setDescription] = useState(
    (props.type === 'edit' ? project?.description : '') ?? '',
  )

  const { create, update } = useProjectsState()

  const submit = async () => {
    try {
      setLoading(true)
      if (!title.trim()) throw new Error('Title must be defined')

      let projectId: number
      if (props.type === 'create') {
        const data = await create(title, description)
        projectId = data.id
      }
      else {
        if (!project) throw new Error('Project must be defined')
        const data = await update(project.id, title, description)
        projectId = data.id
      }
      form.setProjectId(projectId)
      props.onSuccess()
    }
    catch (e) {
      // TODO: Show notification
      console.error(e)
    }
    finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Popup.Title>
        {props.type === 'create' ? 'Create project' : 'Edit project'}
      </Popup.Title>

      <Popup.Fields>
        {props.type === 'create' && (
          <Popup.Msg>
            Create a project to organize your posts and give AI context about what you're building.
          </Popup.Msg>
        )}

        <Popup.Field label='Name'>
          <Input
            disabled={loading}
            placeholder='Acme'
            value={title}
            onChange={value => setTitle(value)}
          />
        </Popup.Field>

        <Popup.Field label='Description'>
          <TextArea
            disabled={loading}
            value={description}
            onChange={value => setDescription(value)}
          />
        </Popup.Field>
      </Popup.Fields>

      <Popup.Footer>
        <Popup.Buttons>
          {project && props.type === 'edit' && (
            <Button onClick={props.onDelete}>
              Delete
            </Button>
          )}
        </Popup.Buttons>

        <Popup.Buttons>
          {props.onCancel && (
            <Button
              onClick={props.onCancel}
            >
              Back
            </Button>
          )}
          <Button
            type='primary'
            disabled={loading || !title.trim()}
            onClick={submit}
          >
            {project && props.type === 'edit' ? 'Save' : 'Create'}
            {loading && (
              <>
                {' '}
                <Loader />
              </>
            )}
          </Button>
        </Popup.Buttons>
      </Popup.Footer>
    </>
  )
}
